/**
 * Sticky podcast player.
 *
 * Podcast cards used to open the same full-screen lightbox as the films, which
 * dimmed the page and locked its scroll around an 80px strip of Spotify. Audio
 * is the one thing you want still running while you read on, so it gets a bar
 * docked to the bottom of the viewport instead and the page is left alone.
 *
 * Deliberately not a dialog: no `modal-is-active` on <html> (base/_base.scss
 * turns that into `overflow: hidden`), no aria-modal, no focus trap. Escape is
 * scoped to the bar rather than bound to the document, or reading further down
 * the page and hitting Escape would kill the episode.
 *
 * The bar is built here rather than in Twig because it is one per page and
 * _media-modals.twig renders once per section — a partial would duplicate it,
 * ids and all, on a hub with two media sections.
 *
 * Playback starts on its own via Spotify's IFrame API. Appending `autoplay=1`
 * to the embed URL does nothing — the player still comes up paused — so pressing
 * play on a card would have cost a second press inside the bar. Anything that is
 * not a Spotify embed falls back to the pasted iframe, which cannot autoplay.
 */

const API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';
/* Spotify lays its player out to the height it is handed. Keep in step with
   $bar-h in styles/molecules/_podcast-bar.scss. */
const HEIGHT = 80;

const BAR_MARKUP = `
  <div class="podbar__inner">
    <div class="podbar__player js-podbar-target"></div>
    <button class="podbar__close js-podbar-close" type="button">
      <span class="visually-hidden">Close player</span>
    </button>
  </div>
`;

let bar = null;
let target = null;
let controller = null;
let apiPromise = null;
let playing = null;
let lastTrigger = null;

/* spotify:episode:xxxx out of an open.spotify.com/embed/episode/xxxx iframe. */
function spotifyUri(template) {
  const iframe = template.content.querySelector('iframe');
  const match = iframe && iframe.getAttribute('src')
    .match(/open\.spotify\.com\/embed\/([a-z]+)\/([A-Za-z0-9]+)/);

  return match ? `spotify:${match[1]}:${match[2]}` : null;
}

/* Loaded on first play, not on page load — the whole point of the <template> is
   that a hub of six episodes fetches nothing from Spotify until asked. */
function loadApi() {
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    window.onSpotifyIframeApiReady = resolve;

    const script = document.createElement('script');
    script.src = API_SRC;
    script.async = true;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return apiPromise;
}

function clearPlayer() {
  if (controller) {
    controller.destroy();
    controller = null;
  }
  target.innerHTML = '';
}

function close() {
  if (!bar) return;

  bar.classList.remove('podbar-is-active');
  document.documentElement.classList.remove('podbar-is-active');

  // Tearing the player down is what stops playback. A bar that slid away while
  // still talking, with no visible control to stop it, would be worse than one
  // that never opened.
  clearPlayer();
  playing = null;

  if (lastTrigger) {
    lastTrigger.focus();
    lastTrigger = null;
  }
}

/* Rebuilt per episode rather than switched with loadUri: playback starts from
   the controller's own `ready` event, which only fires for a fresh one. */
function playSpotify(uri) {
  return loadApi().then((api) => {
    clearPlayer();

    const host = document.createElement('div');
    target.appendChild(host);

    api.createController(host, { uri, height: HEIGHT }, (ctrl) => {
      controller = ctrl;
      ctrl.addListener('ready', () => ctrl.play());
    });
  });
}

function playRaw(template) {
  clearPlayer();
  target.appendChild(template.content.cloneNode(true));
}

function build() {
  bar = document.createElement('div');
  bar.className = 'podbar';
  bar.setAttribute('role', 'region');
  bar.setAttribute('aria-label', 'Podcast player');
  bar.innerHTML = BAR_MARKUP;

  target = bar.querySelector('.js-podbar-target');
  bar.querySelector('.js-podbar-close').addEventListener('click', close);
  bar.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  document.body.appendChild(bar);
}

function open(trigger) {
  const source = document.querySelector(trigger.getAttribute('href'));
  if (!source) return;

  if (!bar) build();

  // One player at a time. Pressing play on a second episode swaps this one out
  // rather than leaving two of them talking over each other.
  if (playing !== source) {
    playing = source;

    const uri = spotifyUri(source);
    if (uri) {
      playSpotify(uri).catch(() => playRaw(source));
    } else {
      playRaw(source);
    }
  }

  bar.classList.add('podbar-is-active');
  document.documentElement.classList.add('podbar-is-active');
  lastTrigger = trigger;

  bar.querySelector('.js-podbar-close').focus();
}

export default function podcastBar() {
  const triggers = document.querySelectorAll('.js-play-podcast');
  if (!triggers.length) return;

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      open(trigger);
    });
  });
}
